/**
 * Logout reason storage helpers.
 *
 * Mirror of `apps/participant/src/lib/logout-reason.ts` — keep the
 * two in sync.
 */

export type LogoutReasonKey =
  | 'account_inactive'
  | 'token_invalid'
  | 'session_expired'
  | 'idle_timeout'
  | 'unknown';

const STORAGE_KEY = 'logout_reason';

export function getLogoutReason(): string | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setLogoutReason(reason: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, reason);
  } catch {
    // Ignore — storage may be disabled.
  }
}

export function clearLogoutReason(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — storage may be disabled.
  }
}

export function resolveLogoutReason(raw: string | null): LogoutReasonKey | null {
  if (!raw) return null;
  const normalised = raw.toLowerCase();

  if (
    normalised.includes('idle') ||
    normalised.includes('inactivity') ||
    normalised.includes('tidak ada aktivitas') ||
    normalised.includes('inaktif')
  ) {
    return 'idle_timeout';
  }

  if (
    normalised.includes('tidak aktif') ||
    normalised.includes('inactive') ||
    normalised.includes('deactivated') ||
    normalised.includes('not active')
  ) {
    return 'account_inactive';
  }

  if (
    normalised.includes('refresh') ||
    normalised.includes('expired') ||
    normalised.includes('kadaluarsa')
  ) {
    return 'session_expired';
  }

  if (
    normalised.includes('token') ||
    normalised.includes('unauthorized') ||
    normalised.includes('tidak valid')
  ) {
    return 'token_invalid';
  }

  return 'unknown';
}
